import Image from "@components/Image";
import Icons from "@gstyles/styleguide/icons";
import useSwiper from "@hooks/useSwiper";
import cx from "classnames";
import { useMemo, useState } from "react";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import styles from "./ImagesGallery.module.scss";

interface Props {
  galleryName: string;
  images: { src: string; alt: string }[];
  className?: string | { [key: string]: string };
  itemClassName?: string | { [key: string]: string };
  imageClassName?: string | { [key: string]: string };
  scrollbar?: { width: number; height: number };
}

const formatPaginationNumber = (
  locales?: string,
  options?: Intl.NumberFormatOptions
) => {
  return (value: number) =>
    new Intl.NumberFormat(
      locales ?? "de-DE",
      options ?? { minimumIntegerDigits: 2 }
    ).format(value);
};

export default function ImageGalleryNavOutside({
  images,
  galleryName,
  className,
  itemClassName,
  imageClassName,
  scrollbar = { width: 170, height: 1 },
}: Props) {
  const [activeIndex, $activeIndex] = useState(0);
  const [xAxis, setXAxis] = useState(0);
  const widthScrollbarDrag = useMemo(
    () => scrollbar.width / images.length,
    [scrollbar, images.length]
  );
  const swiperEl = useSwiper(`.${galleryName}`, {
    rewind: true,
    speed: 1000,
    parallax: true,
    slidesPerView: 1,
    grabCursor: false,
    centeredSlides: true,
    pagination: {
      el: `.swiper-${galleryName}-pagination`,
      type: "bullets",
      clickable: true,
    },
    navigation: {
      nextEl: `.swiper-${galleryName}-next`,
      prevEl: `.swiper-${galleryName}-prev`,
    },
    on: {
      slideChange: (swiper) => {
        if (swiper) {
          setXAxis(widthScrollbarDrag * (swiper?.realIndex ?? 0));
          $activeIndex(swiper.realIndex);
          swiper.pagination.render();
          swiper.pagination.update();
        }
      },
    },
  });
  const formatFractionNumber = formatPaginationNumber();
  const onClickPrevChevron = () => {
    const newIndex = activeIndex - 1;
    const lastIndex = images.length - 1;
    swiperEl?.slideTo?.(newIndex < 0 ? lastIndex : newIndex);
  };
  const onClickNextChevron = () => {
    const newIndex = activeIndex + 1;
    const lastIndex = images.length - 1;
    swiperEl?.slideTo?.(newIndex > lastIndex ? 0 : newIndex);
  };
  return (
    <div className={cx(styles.section, className)}>
      <div className={cx(styles.swiperBox)}>
        <Icons
          name="chevron-left-outline"
          color={"#af915e"}
          className={cx(styles.prevChevronIcon, {
            [styles.outside]: true,
          })}
          onClick={onClickPrevChevron}
        />
        <div className={cx(styles.swiper, `${galleryName}`, "swiper")}>
          <div className={cx("swiper-wrapper")}>
            {images.map((img, index) => {
              return (
                <div
                  key={`${index}_${img.alt}`}
                  className={cx("swiper-slide", styles.item, itemClassName)}
                >
                  <div className={cx(styles.wrapperImage)}>
                    <Image
                      scale={1.08}
                      wrapperProps={{
                        className: cx(styles.image, imageClassName),
                      }}
                      src={img.src}
                      alt={img.alt}
                      layout="fill"
                      objectFit="cover"
                    />
                    <div className={cx(styles.bgGradient)} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className={cx(styles.pagination)}>
            <div className={cx(styles.fraction)}>{`${formatFractionNumber(
              activeIndex + 1
            )} / ${formatFractionNumber(images.length)}`}</div>
            <div
              className={cx(`swiper-${galleryName}-pagination`, styles.bullets)}
            />
          </div>
          <div className={cx(styles.navigation)}>
            <Icons
              name="arrow-left-outline"
              color="#FFFFFF"
              className={cx(`swiper-${galleryName}-prev`, styles.prevArrowIcon)}
            />
            <Icons
              name="arrow-right-outline"
              color="#FFFFFF"
              className={cx(`swiper-${galleryName}-next`, styles.nextArrowIcon)}
            />
          </div>
        </div>
        <Icons
          name="chevron-right-outline"
          color={"#af915e"}
          className={cx(styles.nextChevronIcon, {
            [styles.outside]: true,
          })}
          onClick={onClickNextChevron}
        />
        <div
          className={cx(styles.scrollbarCustom)}
          style={{
            width: `${scrollbar.width}px`,
            height: `${scrollbar.height}px`,
          }}
        >
          <div
            className={cx(styles.scrollbarDrag)}
            style={{
              width: `${widthScrollbarDrag}px`,
              transform: `translate3d(${xAxis}px, 0px, 0px)`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
